import React, { useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  SkeletonLine,
  useBreakpoint,
} from '~/techstyle-shared/react-components';

import useFadeOutUnmount from '../../hooks/useFadeOutUnmount';
import {
  buildFallbackImageFromCDN,
  getProductImageAspectRatioString,
  getProductImageSizeHeight,
  getProductImageSizeString,
  getProductImageSizeWidth,
  PRODUCT_IMAGE_ANGLE_TYPES,
} from '../../utils/images';

import ProductCardImageCarousel from './ProductCardImageCarousel';

const ProductCardImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: ${(props) => props.$aspectRatio};
`;

const StyledSkeletonLine = styled(SkeletonLine)`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  transition: opacity 0.5s;
  opacity: ${({ $isLoading }) => ($isLoading ? 1 : 0)};
`;

const StyledImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.5s;
  opacity: ${({ $isLoading }) => ($isLoading ? 0 : 1)};
`;

const ProductCardImageFromImageService = ({
  imageServiceUrl,
  imagesSummary,
  isFirstProductCard,
  swatchLabel,
  showPlusSizeImage = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isMobile } = useBreakpoint();
  const [isLoading, setIsLoading] = useState(true);
  const { isMounted: showSkeleton, onTransitionEnd: onSkeletonTransitionEnd } =
    useFadeOutUnmount(isLoading);

  const imageServiceParams = new URLSearchParams();
  imageServiceParams.set('model', showPlusSizeImage ? 'p' : 'd'); // p = plus, d = default
  imageServiceParams.set('imgSize', getProductImageSizeString(isMobile));
  const imageServiceParamString = imageServiceParams.toString();
  const imageVersionsFromSummary = showPlusSizeImage
    ? (imagesSummary?.counts?.plusDetails?.imageVersions ?? [])
    : (imagesSummary?.counts?.defaultDetails?.imageVersions ?? []);

  // Remove extraneous https:// or extra // from the image service url (comes from config)
  // without this the urls can be invalid but work (e.g. `https:////`)
  // or invalid and break (e.g. `https://https://`)
  const cleanedImageServiceUrl = imageServiceUrl?.replace(
    /^(https:?\/\/|\/\/)+/,
    ''
  );
  const buildImageServiceUrl = (version) =>
    `https://${cleanedImageServiceUrl}?${imageServiceParamString}&version=${version}`;

  const primaryImageUrl = buildImageServiceUrl(
    PRODUCT_IMAGE_ANGLE_TYPES.PRIMARY
  );
  const hoverImageUrl = buildImageServiceUrl(
    PRODUCT_IMAGE_ANGLE_TYPES.SECONDARY
  );
  const imageUrls = imageVersionsFromSummary.map((version) =>
    buildImageServiceUrl(version)
  );

  const handleImageLoaded = () => {
    setIsLoading(false);
  };

  const handleFirstCarouselImageLoaded = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleImageError = (e) => {
    const fallbackImageUrl = buildFallbackImageFromCDN({
      requestedSize: getProductImageSizeString(isMobile),
    });

    // Infinite loop occurs if the fallback image also fails to load
    // We need to make sure the fallback is only set once
    if (e.target.src !== fallbackImageUrl) {
      e.target.onerror = null; // prevents looping
      e.target.src = fallbackImageUrl;
    }
  };

  // Preload the hover image (desktop only) during a browser idle window so the
  // preload doesn't compete with active scroll/paint work. Cancels on unmount
  // or URL change so cards that scroll offscreen before the browser idles
  // never spend network/decode work on their hover image.
  useEffect(() => {
    if (isMobile || typeof window === 'undefined') {
      return undefined;
    }
    // Safari has no requestIdleCallback — fall back to a longer setTimeout
    // so fast-scrolled cards still get a chance to unmount and cancel before
    // their preload fires.
    const schedule =
      window.requestIdleCallback ?? ((cb) => window.setTimeout(cb, 500));
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    const id = schedule(() => {
      const image = new Image();
      image.src = hoverImageUrl;
    });
    return () => cancel(id);
  }, [hoverImageUrl, isMobile]);

  if (isMobile && imageVersionsFromSummary.length > 1) {
    return (
      <ProductCardImageCarousel
        imageUrls={imageUrls}
        isMobile={isMobile}
        isFirstProductCard={isFirstProductCard}
        swatchLabel={swatchLabel}
        onFirstImageLoaded={handleFirstCarouselImageLoaded}
      />
    );
  }

  return (
    <ProductCardImageContainer
      $aspectRatio={getProductImageAspectRatioString(isMobile)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showSkeleton && (
        <StyledSkeletonLine
          $isLoading={isLoading}
          $height={getProductImageSizeHeight(isMobile)}
          $width={getProductImageSizeWidth(isMobile)}
          onTransitionEnd={onSkeletonTransitionEnd}
        />
      )}
      <StyledImage
        $isLoading={isLoading}
        src={isHovered ? hoverImageUrl : primaryImageUrl}
        alt={swatchLabel}
        onError={handleImageError}
        onLoad={handleImageLoaded}
        data-autotag={isHovered ? 'product-image-on-hover' : 'product-image'}
      />
    </ProductCardImageContainer>
  );
};

ProductCardImageFromImageService.propTypes = {
  imageServiceUrl: PropTypes.string.isRequired,
  imagesSummary: PropTypes.shape({
    counts: PropTypes.shape({
      defaultDetails: PropTypes.shape({
        imageVersions: PropTypes.arrayOf(PropTypes.number),
      }),
      plusDetails: PropTypes.shape({
        imageVersions: PropTypes.arrayOf(PropTypes.number),
      }),
    }),
  }),
  isFirstProductCard: PropTypes.bool,
  showPlusSizeImage: PropTypes.bool,
  swatchLabel: PropTypes.string,
};

export default ProductCardImageFromImageService;
